package com.unrealmi.site.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import com.unrealmi.site.AuroraAnim
import com.unrealmi.site.components.sections.Footer
import com.unrealmi.site.components.sections.NavHeader
import com.unrealmi.site.toSitePalette
import com.varabyte.kobweb.core.rememberPageContext
import com.mrljdx.firebase.analytics.Analytics
import com.mrljdx.firebase.app.FirebaseApp
import com.mrljdx.firebase.app.FirebaseOptions
import kotlinx.browser.window

class PageLayoutData(val title: String)

/**
 * A fixed, full-viewport layer that renders the animated "tech" backdrop: a faint grid plus a couple of
 * slowly drifting colored glows. It sits behind everything and never intercepts pointer events.
 */
@Composable
private fun TechBackground() {
    val palette = ColorMode.current.toSitePalette()
    val gridColor = palette.grid.toRgb()
    val gridCss = "rgba(${gridColor.red}, ${gridColor.green}, ${gridColor.blue}, ${gridColor.alpha / 255f})"

    Box(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px)
            .fillMaxSize()
            .zIndex(-1)
            .pointerEvents(PointerEvents.None)
            .overflow(Overflow.Hidden)
    ) {
        // Faint grid.
        Div(
            Modifier
                .position(Position.Absolute)
                .fillMaxSize()
                .styleModifier {
                    property(
                        "background-image",
                        "linear-gradient($gridCss 1px, transparent 1px), " +
                            "linear-gradient(90deg, $gridCss 1px, transparent 1px)"
                    )
                    property("background-size", "56px 56px")
                    // Fade the grid out toward the edges so it doesn't feel like a hard sheet.
                    property(
                        "-webkit-mask-image",
                        "radial-gradient(ellipse 90% 70% at 50% 12%, black 30%, transparent 78%)"
                    )
                    property(
                        "mask-image",
                        "radial-gradient(ellipse 90% 70% at 50% 12%, black 30%, transparent 78%)"
                    )
                }
                .toAttrs()
        )

        // Two drifting aurora glows.
        Div(
            Modifier
                .position(Position.Absolute)
                .top((-15).percent).left((-10).percent)
                .size(45.cssRem)
                .borderRadius(50.percent)
                .styleModifier {
                    property("background", "radial-gradient(circle, rgba(124,92,255,0.28) 0%, transparent 65%)")
                    property("filter", "blur(20px)")
                }
                .animation(AuroraAnim.toAnimation(duration = 18.s, iterationCount = AnimationIterationCount.Infinite, timingFunction = AnimationTimingFunction.EaseInOut))
                .toAttrs()
        )
        Div(
            Modifier
                .position(Position.Absolute)
                .top(20.percent).right((-12).percent)
                .size(38.cssRem)
                .borderRadius(50.percent)
                .styleModifier {
                    property("background", "radial-gradient(circle, rgba(72,33,203,0.24) 0%, transparent 65%)")
                    property("filter", "blur(20px)")
                }
                .animation(AuroraAnim.toAnimation(duration = 24.s, iterationCount = AnimationIterationCount.Infinite, timingFunction = AnimationTimingFunction.EaseInOut))
                .toAttrs()
        )
    }
}

@Composable
@Layout
fun PageLayout(ctx: PageContext, content: @Composable ColumnScope.() -> Unit) {
    val app = remember {
        FirebaseApp.initialize(
            FirebaseOptions(
                apiKey = "AIzaSyDp4wR7bEoyu-eXZ1y9XGGGDecjS6RTfjI",
                authDomain = "unrealmisite.firebaseapp.com",
                databaseURL = "https://unrealmisite-default-rtdb.firebaseio.com",
                projectId = "unrealmisite",
                storageBucket = "unrealmisite.firebasestorage.app",
                messagingSenderId = "554672278802",
                appId = "1:753961642738:web:6950b65d4c31097d403e6b",
                measurementId = "G-T377S2017C",
            ),
        )
    }
    val analytics = remember { app.getAnalytics() }

    val data = ctx.data.getValue<PageLayoutData>()
    LaunchedEffect(data.title) {
        document.title = "UnrealMi 虚米网络 - ${data.title}"
    }

    if (window.location.hostname != "localhost") {
        val context = rememberPageContext()
        LaunchedEffect(context) { // Context changing means we definitely visited a new page
            analytics.log(Analytics.Event.PageView())
        }
    }

    TechBackground()

    Column(
        Modifier
            .fillMaxWidth()
            .minHeight(100.vh),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        NavHeader()
        // Content fills remaining space and manages its own inner width. Home sections go full-bleed;
        // the markdown layout wraps itself in a narrower readable column.
        Column(
            Modifier.fillMaxWidth().flexGrow(1),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            content()
        }
        Footer(Modifier.fillMaxWidth())
    }
}
